import { styled, alpha } from '@mui/material/styles';
import { colors } from '../../../../styles/theme';
import {
  TableContainer,
  Paper,
  TableHead,
  TableRow,
  Typography,
  TypographyProps,
  Chip,
  IconButton,
  Box,
} from '@mui/material';

export const ModernTableContainer = styled(TableContainer)(({ theme }) => ({
  background: 'transparent',
  boxShadow: 'none',
  borderRadius: theme.spacing(3),
  padding: theme.spacing(0, 0, 2, 0),
}));

export const ModernPaper = styled(Paper)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: theme.spacing(3),
  ...(theme.palette.mode === 'dark'
    ? {
        boxShadow: 'none',
        border: `1px solid ${theme.palette.divider}`,
      }
    : {
        boxShadow: '0 4px 32px 0 rgba(16,30,54,0.08)',
      }),
  padding: theme.spacing(0, 0, 2, 0),
}));

export const ModernTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  '& .MuiTableCell-root': {
    color: theme.palette.text.secondary,
    fontWeight: 600,
    fontSize: '0.95rem',
    letterSpacing: 0.8,
    textTransform: 'capitalize',
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: 'transparent',
    paddingTop: theme.spacing(0.8),
    paddingBottom: theme.spacing(0.8),
    paddingLeft: 0,
    paddingRight: 0,
  },
}));

export const ModernTableRow = styled(TableRow)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(2),
  transition: 'background 0.2s',
  '& .MuiTableCell-root': {
    borderBottom: 'none',
    fontSize: '1rem',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingLeft: 0,
    paddingRight: 0,
    background: 'transparent',
  },
}));

export const StatusChip = styled(Chip)<{ color: 'success' | 'warning' | 'error' | 'default' }>(
  ({ theme, color }) => ({
    fontWeight: 600,
    fontSize: '0.85rem',
    borderRadius: theme.spacing(2.5),
    paddingX: theme.spacing(1.5),
    height: 26,
    backgroundColor:
      color === 'success'
        ? `${colors.success.light}20`
        : color === 'warning'
          ? `${colors.warning.light}20`
          : color === 'error'
            ? `${colors.error.light}20`
            : theme.palette.mode === 'dark'
              ? theme.palette.action.hover
              : colors.grey[100],
    color:
      color === 'success'
        ? colors.success.dark
        : color === 'warning'
          ? colors.warning.dark
          : color === 'error'
            ? colors.error.dark
            : theme.palette.mode === 'dark'
              ? theme.palette.text.secondary
              : colors.grey[700],
    letterSpacing: 0.5,
    minWidth: 75,
    textAlign: 'center',
    border: 'none',
    '& .MuiChip-label': {
      paddingX: theme.spacing(2),
    },
  }),
);

// Styled IconButton base
export const StyledIconButton = styled(IconButton)(({ theme }) => ({
  padding: '4px',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

// Styled IconButton para ver
export const ViewIconButton = styled(StyledIconButton)(({ theme }) => ({
  color: '#2563eb',
  '&:hover': {
    color: '#1d4ed8',
    backgroundColor: 'rgba(37, 99, 235, 0.12)',
  },
}));

// Styled IconButton para procesar pago
export const PaymentIconButton = styled(StyledIconButton)(({ theme }) => ({
  color: '#2563eb',
  '&:hover': {
    color: '#1d4ed8',
    backgroundColor: 'rgba(37, 99, 235, 0.12)',
  },
}));

// Styled IconButton para devolución
export const ReturnIconButton = styled(StyledIconButton)(({ theme }) => ({
  color: '#2563eb',
  '&:hover': {
    color: '#1d4ed8',
    backgroundColor: 'rgba(37, 99, 235, 0.12)',
  },
}));

// Styled IconButton para cancelar
export const CancelIconButton = styled(StyledIconButton)(({ theme }) => ({
  color: '#2563eb',
  '&:hover': {
    color: '#1d4ed8',
    backgroundColor: 'rgba(37, 99, 235, 0.12)',
  },
}));

// Mantener ActionIconButton para compatibilidad (deprecated, usar los específicos)
interface ActionIconButtonProps {
  color?: 'primary' | 'info' | 'error';
}

export const ActionIconButton = styled(IconButton)<ActionIconButtonProps>(({ theme, color }) => {
  let paletteColor = theme.palette.primary;
  if (color === 'info') paletteColor = theme.palette.info;
  if (color === 'error') paletteColor = theme.palette.error;
  if (color === 'primary') paletteColor = theme.palette.primary;

  return {
    background: alpha(paletteColor.main, 0.08),
    color: paletteColor.main,
    borderRadius: '50%',
    transition: 'all 0.18s',
    '&:hover': {
      background: alpha(paletteColor.main, 0.18),
      transform: 'scale(1.13)',
      boxShadow: '0 2px 8px 0 rgba(16,30,54,0.10)',
    },
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
    padding: 6,
  };
});

const orderCodeTextStyles = ({
  theme,
}: {
  theme: { palette: { text: { secondary: string } } };
}) => ({
  fontWeight: 400,
  color: theme.palette.text.secondary,
  fontSize: '0.875rem', // Mismo tamaño que variant="body2" usado en Sucursal
  letterSpacing: 0.2,
});

/** Texto de código de venta en tablas (solo lectura). */
export const OrderCode = styled(Typography)(({ theme }) => orderCodeTextStyles({ theme }));

/** Código de venta clickeable; tipado para `component="button"` y `type="button"`. */
export const OrderCodeButton = styled(Typography)<TypographyProps<'button'>>(({ theme }) => ({
  fontWeight: 500,
  fontSize: '0.875rem',
  letterSpacing: 0.2,
  color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main,
  border: 'none',
  background: 'none',
  padding: 0,
  cursor: 'pointer',
  textAlign: 'left',
  font: 'inherit',
  textDecoration: 'none',
  '&:hover': {
    color: theme.palette.primary.main,
    textDecoration: 'underline',
  },
}));

export const TotalText = styled(Typography)(({ theme }) => ({
  fontWeight: 400,
  color: theme.palette.mode === 'dark' ? theme.palette.success.light : theme.palette.success.dark,
  fontSize: '0.875rem',
}));

export const DateText = styled(Typography)(({ theme }) => ({
  fontWeight: 400,
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  letterSpacing: 0.5,
}));

export const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(4),
}));

export const ErrorContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
}));

export const EmptyContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(6),
  textAlign: 'center',
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1),
  border: `2px dashed ${theme.palette.divider}`,
}));

export const ActionsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(0.5),
}));
